import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { UploadFileOutlined } from "@mui/icons-material";
import MarkdownView from "../../components/MarkdownView";
import { extractTitle, normalizeMarkdown } from "../../common/cookbook";

// Add a cookbook entry (entry = null) or edit an existing one. The Markdown can be
// pasted, typed, uploaded or dropped in as a .md file.
const EntryDialog = ({ open, entry, categories, onClose, onSave, saving }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInput = useRef(null);
  const [title, setTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);
  const [category, setCategory] = useState("");
  const [content, setContent] = useState("");
  const [tab, setTab] = useState("write");
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTitle(entry ? entry.title : "");
      setTitleEdited(Boolean(entry));
      setCategory(entry ? entry.category : "");
      setContent(entry ? entry.content : "");
      setTab("write");
      setDragging(false);
      setError("");
    }
  }, [open, entry]);

  // Until a title is typed by hand, it follows the note's "# Heading".
  const changeContent = (value, fallbackTitle = "") => {
    setContent(value);
    if (!titleEdited) {
      setTitle(extractTitle(value) || fallbackTitle);
    }
  };

  const readFile = (file) => {
    if (!file) {
      return;
    }
    if (!/\.(md|markdown|txt)$/i.test(file.name) && !file.type.startsWith("text/")) {
      setError("Choose a Markdown (.md) or text file.");
      return;
    }
    file
      .text()
      .then((text) => {
        changeContent(normalizeMarkdown(text), file.name.replace(/\.[^.]+$/, ""));
        setError("");
      })
      .catch(() => setError("Could not read that file."));
  };

  const handleDrop = (e) => {
    setDragging(false);
    if (e.dataTransfer.files.length) {
      e.preventDefault();
      readFile(e.dataTransfer.files[0]);
    }
  };

  const handleSave = () => {
    const markdown = normalizeMarkdown(content);
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!markdown) {
      setError("Paste or upload the note's Markdown.");
      return;
    }
    onSave({ title: title.trim(), category: category.trim(), content: markdown })
      .then(onClose)
      .catch((err) => setError(err?.data?.message || "Failed to save the entry."));
  };

  const mono = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>{entry ? "Edit topic" : "Add topic"}</DialogTitle>
      <DialogContent>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "3fr 2fr" }}
          gap="1rem"
          sx={{ mt: "0.5rem" }}
        >
          <TextField
            label="Title"
            placeholder="e.g. Diverticulitis"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleEdited(true);
            }}
          />
          <Autocomplete
            freeSolo
            options={categories || []}
            inputValue={category}
            onInputChange={(e, value) => setCategory(value)}
            renderInput={(params) => <TextField {...params} label="Category" placeholder="e.g. Colorectal" />}
          />
        </Box>

        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="0.5rem" mt="1rem">
          <Tabs value={tab} onChange={(e, value) => setTab(value)} textColor="secondary" indicatorColor="secondary">
            <Tab value="write" label="Markdown" />
            <Tab value="preview" label="Preview" />
          </Tabs>
          <Button size="small" variant="outlined" color="secondary" startIcon={<UploadFileOutlined />} onClick={() => fileInput.current.click()}>
            Upload .md
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".md,.markdown,.txt,text/markdown,text/plain"
            hidden
            onChange={(e) => {
              readFile(e.target.files[0]);
              e.target.value = "";
            }}
          />
        </Box>

        {tab === "write" ? (
          <Box
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes("Files")) {
                e.preventDefault();
                setDragging(true);
              }
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            sx={{
              mt: "0.75rem",
              borderRadius: "10px",
              outline: dragging ? `2px dashed ${theme.palette.secondary.main}` : "none",
              backgroundColor: dragging ? alpha(theme.palette.secondary.main, 0.06) : "transparent",
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={14}
              maxRows={22}
              value={content}
              onChange={(e) => changeContent(e.target.value)}
              placeholder="Paste the Markdown your AI generated here, or drop a .md file onto this box."
              InputProps={{ sx: { fontFamily: mono, fontSize: "0.82rem", lineHeight: 1.6 } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              mt: "0.75rem",
              p: "1.25rem 1.5rem",
              minHeight: "20rem",
              maxHeight: fullScreen ? "none" : "55vh",
              overflowY: "auto",
              borderRadius: "10px",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {normalizeMarkdown(content) ? (
              <MarkdownView>{normalizeMarkdown(content)}</MarkdownView>
            ) : (
              <Typography color="text.secondary">Nothing to preview yet.</Typography>
            )}
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mt: "0.75rem" }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntryDialog;
